<script setup>
import { onMounted, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';

import ContactsDetailsLayout from 'dashboard/components-next/Contacts/ContactsDetailsLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ContactDetails from 'dashboard/components-next/Contacts/Pages/ContactDetails.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import ContactNotes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactNotes.vue';
import ContactHistory from 'dashboard/components-next/Contacts/ContactsSidebar/ContactHistory.vue';
import ContactMedia from 'dashboard/components-next/Contacts/ContactsSidebar/ContactMedia.vue';
import ContactMerge from 'dashboard/components-next/Contacts/ContactsSidebar/ContactMerge.vue';
import ContactCustomAttributes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactCustomAttributes.vue';
import { useActivitiesStore } from 'dashboard/stores/activities';
import { useDealsStore } from 'dashboard/stores/deals';

const store = useStore();
const route = useRoute();
const router = useRouter();

const activitiesStore = useActivitiesStore();
const dealsStore = useDealsStore();

const contact = useMapGetter('contacts/getContactById');
const uiFlags = useMapGetter('contacts/getUIFlags');

const activeTab = ref('attributes');
const contactMergeRef = ref(null);

const isFetchingItem = computed(() => uiFlags.value.isFetchingItem);
const isMergingContact = computed(() => uiFlags.value.isMerging);
const isUpdatingContact = computed(() => uiFlags.value.isUpdating);

const selectedContact = computed(() => contact.value(route.params.contactId));

const showSpinner = computed(
  () => isFetchingItem.value || isMergingContact.value
);

const { t } = useI18n();

const CONTACT_TABS_OPTIONS = [
  { key: 'ATTRIBUTES', value: 'attributes' },
  { key: 'HISTORY', value: 'history' },
  { key: 'NOTES', value: 'notes' },
  { key: 'MEDIA', value: 'media' },
  { key: 'MERGE', value: 'merge' },
  { key: 'DEALS', value: 'deals' },
  { key: 'ACTIVITIES', value: 'activities' },
];

const tabs = computed(() => {
  return CONTACT_TABS_OPTIONS.map(tab => ({
    label: t(`CONTACTS_LAYOUT.SIDEBAR.TABS.${tab.key}`),
    value: tab.value,
  }));
});

const activeTabIndex = computed(() => {
  return CONTACT_TABS_OPTIONS.findIndex(v => v.value === activeTab.value);
});

const goToContactsList = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(`/app/accounts/${route.params.accountId}/contacts?page=1`);
  }
};

const fetchActiveContact = async () => {
  if (route.params.contactId) {
    await store.dispatch('contacts/show', { id: route.params.contactId });
    await store.dispatch(
      'contacts/fetchContactableInbox',
      route.params.contactId
    );
  }
};

const handleTabChange = tab => {
  activeTab.value = tab.value;
};

const fetchContactNotes = () => {
  const { contactId } = route.params;
  if (contactId) store.dispatch('contactNotes/get', { contactId });
};

const fetchContactConversations = () => {
  const { contactId } = route.params;
  if (contactId) store.dispatch('contactConversations/get', contactId);
};

const fetchAttributes = () => {
  store.dispatch('attributes/get');
};

const contactDeals = computed(() => dealsStore.getRecords);
const contactActivities = computed(() => activitiesStore.getActivitiesList);

const fetchContactDeals = () => {
  const { contactId } = route.params;
  if (contactId) dealsStore.get({ contact_id: contactId });
};

const fetchContactActivities = () => {
  const { contactId } = route.params;
  if (contactId) activitiesStore.get({ contact_id: contactId });
};

const toggleContactBlock = async isBlocked => {
  const ALERT_MESSAGES = {
    success: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_SUCCESS_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_SUCCESS_MESSAGE'),
    },
    error: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_ERROR_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_ERROR_MESSAGE'),
    },
  };

  try {
    await store.dispatch(`contacts/update`, {
      ...selectedContact.value,
      blocked: !isBlocked,
    });
    useAlert(
      isBlocked ? ALERT_MESSAGES.success.unblock : ALERT_MESSAGES.success.block
    );
  } catch (error) {
    useAlert(
      isBlocked ? ALERT_MESSAGES.error.unblock : ALERT_MESSAGES.error.block
    );
  }
};

onMounted(() => {
  fetchActiveContact();
  fetchContactNotes();
  fetchContactConversations();
  fetchAttributes();
  fetchContactDeals();
  fetchContactActivities();
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-surface-1"
  >
    <ContactsDetailsLayout
      :button-label="$t('CONTACTS_LAYOUT.HEADER.SEND_MESSAGE')"
      :selected-contact="selectedContact"
      is-detail-view
      :show-pagination-footer="false"
      :is-updating="isUpdatingContact"
      @go-to-contacts-list="goToContactsList"
      @toggle-block="toggleContactBlock"
    >
      <div
        v-if="showSpinner"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>
      <ContactDetails
        v-else-if="selectedContact"
        :selected-contact="selectedContact"
        @go-to-contacts-list="goToContactsList"
      />
      <template #sidebarHeader>
        <div class="px-6 pt-6 pb-3">
          <TabBar
            :tabs="tabs"
            :initial-active-tab="activeTabIndex"
            class="w-full [&>button]:w-full bg-n-alpha-black2"
            @tab-changed="handleTabChange"
          />
        </div>
      </template>
      <template #sidebar>
        <div
          v-if="isFetchingItem"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>
        <template v-else>
          <ContactCustomAttributes
            v-if="activeTab === 'attributes'"
            :selected-contact="selectedContact"
          />
          <ContactNotes v-if="activeTab === 'notes'" />
          <ContactHistory v-if="activeTab === 'history'" />
          <ContactMedia v-if="activeTab === 'media'" />
          <ContactMerge
            v-if="activeTab === 'merge'"
            ref="contactMergeRef"
            :selected-contact="selectedContact"
            @go-to-contacts-list="goToContactsList"
            @reset-tab="handleTabChange(CONTACT_TABS_OPTIONS[0])"
          />
          <div v-if="activeTab === 'deals'" class="p-6 flex flex-col gap-3">
            <div
              v-if="contactDeals.length === 0"
              class="flex flex-col items-center justify-center py-12 text-slate-500"
            >
              <span class="i-lucide-briefcase size-6 mb-2 text-slate-400" />
              <p class="text-sm text-slate-500">
                {{ t('CONTACTS_LAYOUT.SIDEBAR.DEALS.EMPTY') }}
              </p>
            </div>
            <div
              v-for="deal in contactDeals"
              :key="deal.id"
              class="flex flex-col gap-1 p-3 rounded-lg border border-slate-200 bg-white"
            >
              <div class="flex items-center justify-between">
                <span class="text-sm font-medium text-slate-900">
                  {{ deal.name }}
                </span>
                <span
                  v-if="deal.status"
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600"
                >
                  {{ deal.status }}
                </span>
              </div>
              <div class="flex items-center gap-3 text-xs text-slate-500">
                <span v-if="deal.amount" class="inline-flex items-center gap-1">
                  <span class="i-lucide-dollar-sign size-3.5" />
                  {{ deal.amount }}
                </span>
                <span
                  v-if="deal.expectedCloseDate"
                  class="inline-flex items-center gap-1"
                >
                  <span class="i-lucide-calendar size-3.5" />
                  {{ deal.expectedCloseDate }}
                </span>
              </div>
            </div>
          </div>
          <div
            v-if="activeTab === 'activities'"
            class="p-6 flex flex-col gap-3"
          >
            <div
              v-if="contactActivities.length === 0"
              class="flex flex-col items-center justify-center py-12 text-slate-500"
            >
              <span class="i-lucide-inbox size-6 mb-2 text-slate-400" />
              <p class="text-sm text-slate-500">
                {{ t('CONTACTS_LAYOUT.SIDEBAR.ACTIVITIES.EMPTY') }}
              </p>
            </div>
            <div
              v-for="activity in contactActivities"
              :key="activity.id"
              class="flex flex-col gap-1 p-3 rounded-lg border border-slate-200 bg-white"
            >
              <div class="flex items-center gap-2">
                <span class="text-sm font-medium text-slate-900">
                  {{ activity.title }}
                </span>
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="{
                    'bg-yellow-100 text-yellow-800':
                      activity.status === 'pending',
                    'bg-green-100 text-green-800':
                      activity.status === 'completed',
                    'bg-orange-100 text-orange-800':
                      activity.status === 'snoozed',
                  }"
                >
                  {{ activity.status }}
                </span>
              </div>
              <div class="flex items-center gap-3 text-xs text-slate-500">
                <span class="inline-flex items-center gap-1">
                  <span class="i-lucide-tag size-3.5" />
                  {{ activity.activityType }}
                </span>
                <span
                  v-if="activity.dueDate"
                  class="inline-flex items-center gap-1"
                >
                  <span class="i-lucide-calendar size-3.5" />
                  {{ new Date(activity.dueDate * 1000).toLocaleString() }}
                </span>
              </div>
            </div>
          </div>
        </template>
      </template>
    </ContactsDetailsLayout>
  </div>
</template>
